import {
  Activity,
  Archive,
  ChevronLeft,
  Mail,
  Settings,
  Trash2,
  UsersRound,
} from "lucide-react";
import { NavLink, Outlet } from "react-router-dom";
import { LoadingScreen } from "@/components/common/loading-screen";
import { Button } from "@/components/ui/button";
import { useCurrentGroup, useGroupPages } from "@/features/groups/group-queries";
import { PageTree } from "@/features/pages/page-tree";
import { canManageGroup } from "@/lib/permissions/permissions";
import { cn } from "@/lib/utils";
import { NotFoundPage } from "@/pages/error-pages";

export function GroupLayout() {
  const { groupId, data, isLoading } = useCurrentGroup();
  const {
    data: pages = [],
    isLoading: pagesLoading,
    isError: pagesError,
    error: pagesQueryError,
    refetch: refetchPages,
  } = useGroupPages(
    groupId,
    data?.group.status === "active" ? data.membership.role : undefined,
  );

  if (isLoading) return <LoadingScreen />;
  if (
    !data ||
    data.group.status !== "active" ||
    data.membership.status !== "active"
  ) {
    return <NotFoundPage />;
  }

  const pageTreeContent = pagesLoading ? (
    <p className="px-2 text-xs text-slate-400">Loading pages…</p>
  ) : pagesError ? (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
      <p className="text-xs text-amber-800">
        Pages could not be refreshed. Saved pages have not been deleted.
      </p>
      <p className="mt-1 break-words text-[10px] text-amber-700">
        {pagesQueryError.message}
      </p>
      <button
        type="button"
        className="mt-2 text-xs font-semibold text-amber-900 underline"
        onClick={() => void refetchPages()}
      >
        Retry
      </button>
    </div>
  ) : (
    <PageTree pages={pages} groupId={groupId} />
  );

  return (
    <div className="grid min-h-[calc(100vh-4rem)] lg:grid-cols-[250px_minmax(0,1fr)]">
      <aside className="hidden border-r border-slate-200 bg-slate-50/80 p-3 lg:block">
        <div className="mb-3 flex items-center gap-2 px-2 py-2">
          <Button variant="ghost" size="icon" asChild>
            <NavLink to="/app" aria-label="Back to workspace">
              <ChevronLeft className="size-4" />
            </NavLink>
          </Button>
          <span className="text-xl">{data.group.icon}</span>
          <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-800">
            {data.group.name}
          </span>
        </div>
        <nav className="mb-5 space-y-1 border-b border-slate-200 pb-4">
          {[
            { to: `/app/groups/${groupId}`, label: "Overview", icon: Archive, end: true },
            { to: `/app/groups/${groupId}/activity`, label: "Activity", icon: Activity },
            { to: `/app/groups/${groupId}/settings/members`, label: "Members", icon: UsersRound },
            { to: `/app/groups/${groupId}/settings/invitations`, label: "Invitations", icon: Mail },
            { to: `/app/groups/${groupId}/trash`, label: "Trash", icon: Trash2 },
            { to: `/app/groups/${groupId}/settings`, label: "Settings", icon: Settings },
          ]
            .filter((item) => item.label !== "Settings" || canManageGroup(data.membership.role))
            .map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100",
                    isActive && "bg-white font-semibold text-indigo-700 shadow-sm",
                  )
                }
              >
                <Icon className="size-4" />
                {label}
              </NavLink>
            ))}
        </nav>
        <div className="mb-2 flex items-center justify-between px-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Pages
          </p>
        </div>
        {pageTreeContent}
      </aside>
      <div className="min-w-0">
        <div className="border-b border-slate-200 bg-white p-3 lg:hidden">
          <details>
            <summary className="flex cursor-pointer list-none items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-sm font-semibold">
              <span>{data.group.icon}</span>
              <span className="min-w-0 flex-1 truncate">{data.group.name}</span>
              <span className="text-xs font-normal text-slate-400">Menu & pages</span>
            </summary>
            <div className="mt-3 max-h-[60vh] overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="mb-3 flex flex-wrap gap-1 border-b border-slate-200 pb-3">
                <Button variant="ghost" size="sm" asChild>
                  <NavLink to={`/app/groups/${groupId}`}>Overview</NavLink>
                </Button>
                <Button variant="ghost" size="sm" asChild>
                  <NavLink to={`/app/groups/${groupId}/activity`}>Activity</NavLink>
                </Button>
                <Button variant="ghost" size="sm" asChild>
                  <NavLink to={`/app/groups/${groupId}/settings/members`}>Members</NavLink>
                </Button>
                <Button variant="ghost" size="sm" asChild>
                  <NavLink to={`/app/groups/${groupId}/trash`}>Trash</NavLink>
                </Button>
              </div>
              {pageTreeContent}
            </div>
          </details>
        </div>
        <Outlet context={{ ...data, pages }} />
      </div>
    </div>
  );
}
